import * as React from "react";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { Helmet } from "react-helmet";
import { NavLink } from "react-router-dom";

// parts:
import Header from "./parts/Header";
import ColorBar from "./parts/ColorBar";
import Nav from "./parts/Nav";
import Main from "./parts/Main";
import Content from "./parts/Content";
import Aside from "./parts/Aside";
import Box from "./parts/Box";
import { EnantisSection } from "./commons/enantis";
import Footer from "./parts/Footer";
import ExtLink from "./parts/ExtLink";
import { CookieConsent } from "@loschmidt/components";

import { ll_fetch, ll_url } from "./tools";
import { toolName, toolTitle, toolVersion, toolKeywords, toolEmail, toolDescription, extraJobs, extraVisitors } from "./settings";

import { IPageComponentPropsData, IPageComponentPropsFunctions } from "./IPageComponentProps";
import JobReportContainer from "./pages/JobPage/containers/JobReportContainer";
import CustomMutantContainer from "./pages/JobPage/containers/CustomMutantContainer";
import DownloadContainer from "./pages/JobPage/containers/DownloadContainer";
import InputJobSettingsContainer from "./pages/InputPage/containers/InputJobSettingsContainer";
import InputJobSettings2Container from "./pages/InputPage/containers/InputJobSettings2Container";

// pages:
import InputPage from "./pages/InputPage";
import SummaryPage from "./pages/SummaryPage";
import JobPage from "./pages/JobPage";
import HelpPage from "./pages/HelpPage";
import ExamplePage from "./pages/ExamplePage";
import AcknowledgementPage from "./pages/AcknowledgementPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";

type IProps = IPageComponentPropsData & IPageComponentPropsFunctions;

interface IState {
    countJobs: number,
    countVisitors: number,
}

export default class PageComponent extends React.Component<IProps, IState> {

    state = {
        countJobs: null,
        countVisitors: null,
    };

    menuItems = [
        {target: ll_url('/'), text: 'Submit new job', onClick: this.props.setInitialInputState},
        
        {target: ll_url('/example'), text: 'Example'},
        {target: ll_url('/acknowledgements'), text: 'Acknowledgements'},
        
    ];

    componentDidMount() {
        ll_fetch('/api/countJobs')
        .then(response => response.json())
        .then((countJobs: number) => this.setState({ countJobs: countJobs + extraJobs }));

        ll_fetch('/api/piwik/visitorsCount')
        .then(response => response.json())
        .then((countVisitors: number) => this.setState({ countVisitors: countVisitors + extraVisitors }));
    }

    render() {
        return <BrowserRouter>

            <div className="ll-page">

                <Helmet titleTemplate={`%s | ${toolName} ${toolVersion} – ${toolTitle}`} defaultTitle={`${toolName} ${toolVersion} – ${toolTitle}`}>
                    <meta name="description" content={toolDescription} />
                    <meta name="keywords" content={toolKeywords} />
                </Helmet>

                <Header onClick={this.props.setInitialInputState} />
                <ColorBar />
                <Nav
                    menu={this.menuItems}
                />
                <Main>
                    <Content>
                        <Route exact path={ll_url('/')} component={InputPage} />
                        <Route path={ll_url('/job-setting')} component={InputJobSettingsContainer} />
                        <Route path={ll_url('/job-setting2')} component={InputJobSettings2Container} />
                        <Route path={ll_url('/summary')} component={SummaryPage} />
                        <Route path={ll_url('/job/:jobId')} component={JobPage} />
                        <Route path={ll_url('/help')} component={HelpPage} />
                        <Route path={ll_url('/example')} component={ExamplePage} />
                        <Route path={ll_url('/acknowledgements')} component={AcknowledgementPage} />
                        <Route path={ll_url('/privacy')} component={PrivacyPage} />
                        <Route path={ll_url('/terms-of-use')} component={TermsOfUsePage} />
                    </Content>
                    <Aside>
                        <Switch>
                            <Route path={ll_url('/job/:jobId')} render={this.renderJobresultsSideBar} />
                            <Route render={this.renderIntroBoxes} />
                        </Switch>
                    </Aside>
                </Main>

                <Footer>
                    <div className="ll-keywords">
                        <p><strong>Keywords:</strong> {toolKeywords}</p>
                        <p><strong>Disclaimer:</strong> {toolName} is a non-profit service to the academic scientific community. The responsibility of the author is limited to applying best efforts in providing a useful service. The authors can not be held liable in any way for the service provided here.</p>
                    </div>
                    <div className="ll-terms-privacy">
                        <p><NavLink to={ll_url('/terms-of-use')}>Terms of use</NavLink> | <NavLink to={ll_url('/privacy')}>Privacy</NavLink></p>
                    </div>
                </Footer>

            </div>

            <CookieConsent />

        </BrowserRouter>
    }

    renderIntroBoxes = () => {
        return <>
            <Box title="References" intro>
                <div className="ll-references">
                Musil M, Jezik A, Horackova J, Borko S, Kabourek P, Damborsky J, Bednar D: FireProt 2.0: web-based platform for the fully automated design of thermostable proteins. Briefings in Bioinformatics 25(1), 2024.
                </div>
            </Box>

            <EnantisSection
              toolName={toolName}
              intro
            />

            <Box title="User statistics" intro>
                <ul>
                    <li>
                        <strong>Number of visitors:</strong> {this.state.countVisitors !== null ? `${this.state.countVisitors}` : '-'}
                    </li>
                    <li>
                        <strong>Number of jobs:</strong> {this.state.countJobs !== null ? `${this.state.countJobs}` : '-'}
                    </li>
                </ul>
            </Box>

            <Box title="Contact" intro>
                <p className="ll-contact">Loschmidt Laboratories</p>
                <ul>
                    <li>
                        <a href={`mailto:${toolEmail}`}>{toolEmail}</a>
                    </li>
                    <li>
                        <ExtLink to="https://loschmidt.chemi.muni.cz/" />
                    </li>
                </ul>
            </Box>

            <Box title="Other tools" intro className="ll-other-tools">
                <ExtLink to="https://loschmidt.chemi.muni.cz/enzymeminer/" className="ll-enzymeminer">
                    <img src={ll_url('/images/logos/enzymeminer.svg')} />
                </ExtLink>
                <ExtLink to="https://loschmidt.chemi.muni.cz/soluprot/" className="ll-soluprot">
                    <img src={ll_url('/images/logos/soluprot.svg')} />
                </ExtLink>
                <ExtLink to="https://loschmidt.chemi.muni.cz/predictsnp/" className="ll-predictsnp">
                    <img src={ll_url('/images/logos/predictsnp.svg')} />
                </ExtLink>
                <ExtLink to="https://loschmidt.chemi.muni.cz/caverweb/" className="ll-caver">
                    <img src={ll_url('/images/logos/caverweb.svg')} />
                </ExtLink>
                <ExtLink to="https://loschmidt.chemi.muni.cz/fireprot/" className="ll-fireprot">
                    <img src={ll_url('/images/logos/fireprot.svg')} />
                </ExtLink>
                <ExtLink to="https://loschmidt.chemi.muni.cz/hotspotwizard/" className="ll-hotspotwizard">
                    <img src={ll_url('/images/logos/hotspotwizard.svg')} />
                </ExtLink>
                <ExtLink to="https://loschmidt.chemi.muni.cz/calfitter/" className="ll-calfitter">
                    <img src={ll_url('/images/logos/calfitter.svg')} />
                </ExtLink>
            </Box>

            <Box title="Acknowledgement">
                <div className="ll-ack-logos">
                    <ExtLink to="https://www.elixir-czech.cz/" className="ll-elixir">
                        <img src={ll_url('/images/logos/elixir.svg')} />
                    </ExtLink>
                    <ExtLink to="https://tacr.gov.cz/" className="ll-tacr">
                        <img src={ll_url('/images/logos/logo_TACR_zakl.png')} />
                    </ExtLink>
                </div>
            </Box>
        </>
    }

    renderJobresultsSideBar = () => {
        return <div><Box title="Report">
            <JobReportContainer />
        </Box>
        {this.props.appState.mainState.resultsLoaded && <>
        <div><DownloadContainer/><br/></div>
        <Box title="Custom Design">
            <CustomMutantContainer />
        </Box>
        </>}
        </div>
    }
}
